<template>
  <tab-layout>
    <li v-for="t in visibleTabs" :key="t.key" slot="tabHeading" class="nav-item">
      <a
        :class="['nav-link', selectedTab === t.key ? 'active' : '']"
        href
        @click.prevent="selectTab(t.key)"
        :id="`${t.key}-tab`"
        data-toggle="tab"
        role="tab"
        :aria-controls="t.key"
        :aria-selected="selectedTab === t.key ? 'true' : 'false'">{{ t.label }}</a>
    </li>
    <div
      v-if="selectedTab === 'loading'"
      slot="tabContent"
      class="tab-pane col-12 col-xl-9 px-0 bg-white rounded mb-4 p-3 p-sm-4 show active"
      id="loading"
      role="tabpanel"
      aria-labelledby="loading-tab">
        <div class="spinner-border text-primary" role="status"><span class="sr-only">Loading...</span></div>
      </div>
    <component
      v-else-if="currentTab"
      :is="currentTab.component"
      v-bind="currentTab.props"
      v-on="currentTab.on || {}"
      slot="tabContent"
      :class="currentTab.wrapperClass"
      :id="currentTab.key"
      role="tabpanel"
      :aria-labelledby="`${currentTab.key}-tab`" />
  </tab-layout>
</template>

<script>
/* Use the "slot" attribute to match the heading and content
 * to their respective fields in the ListingLayout component. */
// /members/member-profile
import tabLayout from '@/components/TabLayout'
import GETSTORE from '@/store/memberProfileStore.js'
import memberProfileDetails from '@/views/Members/Tabs/Details'
import memberProfileFinancials from '@/views/Members/Tabs/Financials'
import memberProfileMembership from '@/views/MyMembership/MyMembershipMembership'
import memberProfileMembershipAdmin from '@/views/Members/Tabs/Membership'
import memberProfileHistory from '@/views/Members/Tabs/MembershipHistory'
import judgePerformance from '@/views/Members/Tabs/JudgePerformance'
import memberProfileBoyHistory from '@/views/Members/Tabs/BOYHistory'
import memberProfileHorses from '@/views/Members/Tabs/Horses'
import memberProfileScore from './MemberProfileScore'

const TAB_PANE_ROW = 'tab-pane row mx-0 show active'
const TAB_PANE_CARD = 'tab-pane col-12 col-xl-9 px-0 bg-white rounded mb-4 p-3 p-sm-4 show active'

export default {
  name: 'MemberProfile',
  props: ['details', 'tab', 'success', 'renewing', 'preselectedYear', 'completePaymentProfile', 'returnTo'],
  mixins: [GETSTORE],
  components: {
    'tab-layout': tabLayout
  },
  data() {
    return {
      selectedTab: this.$route.hash ? this.$route.hash.slice(1) : (this.tab || 'details'),
      renew: this.renewing ? this.renewing : false,
      userRole: localStorage.role,
      child: this.details ? this.details.primary ? true : false : false
    }
  },
  mounted() {
    if (!this.renewing) {
      // Fresh visit (not coming back from the cart) — selection made for another member must not carry over
      this.$store.commit('deselectAllRows')
      if (this.child) {
        this.$store.commit('updateViewedMember', '')
      } else {
        this.$store.commit('updateChildMember', '')
      }
    }
    if (this.$route.params && this.$route.params.score) { // Go to score tab if coming from another score tab
      this.selectTab('score')
    }
  },
  computed: {
    showJudgePerformanceRating() {
      return Boolean(this.memberDetails.judge?.id)
    },
    hasMemberDetails() {
      return Boolean(this.memberDetails)
    },
    isPrivileged() {
      return this.userRole === 'Admin' || this.userRole === 'SuperAdmin'
    },
    isSecretaryOrAdmin() {
      return this.isPrivileged || this.userRole === 'Secretary' || this.userRole === 'SecretaryOnly'
    },
    isMemberOrAbove() {
      return this.isPrivileged || this.userRole === 'Member'
    },
    isNotVolunteer() {
      return this.hasMemberDetails && this.memberDetails.getedRole !== 'Volunteer'
    },
    // Single source of truth for every tab on this page — add/remove/reorder tabs here only.
    tabs() {
      return [
        {
          key: 'details',
          label: 'Details',
          component: memberProfileDetails,
          show: true,
          props: {
            memberDetails: this.memberDetails,
            completePaymentProfile: this.completePaymentProfile,
            returnTo: this.returnTo
          },
          on: { renew: () => { this.renew = true; this.selectTab('membership') } },
          wrapperClass: TAB_PANE_ROW
        },
        {
          key: 'membership',
          label: 'Membership',
          component: (this.isPrivileged && !this.renew) ? memberProfileMembershipAdmin : memberProfileMembership,
          show: this.hasMemberDetails,
          props: {
            my: false,
            child: this.child,
            memberDetails: this.memberDetails,
            preselectedYear: this.preselectedYear,
            success: this.success,
            renew: this.renew
          },
          on: { noRenew: () => { this.renew = false } },
          wrapperClass: TAB_PANE_ROW
        },
        {
          key: 'financials',
          label: 'Financials',
          component: memberProfileFinancials,
          show: this.hasMemberDetails && this.isMemberOrAbove && this.isNotVolunteer,
          props: { memberID: this.memberDetails.id },
          wrapperClass: TAB_PANE_CARD
        },
        {
          key: 'score',
          label: 'Results',
          component: memberProfileScore,
          show: this.hasMemberDetails,
          props: {},
          wrapperClass: TAB_PANE_CARD
        },
        {
          key: 'history',
          label: 'History',
          component: memberProfileHistory,
          show: this.hasMemberDetails && this.isMemberOrAbove && this.isNotVolunteer,
          props: { memberID: this.memberDetails.id, membership_options: this.memberDetails.membership_options },
          wrapperClass: TAB_PANE_CARD
        },
        {
          key: 'horses',
          label: 'Horses',
          component: memberProfileHorses,
          show: this.hasMemberDetails && this.isSecretaryOrAdmin,
          props: { memberId: this.memberDetails.id },
          // Wider than the other CARD tabs (col-xl-9 cap) — the filter bar + table need the room,
          // same reasoning as Details/Membership. Horses.vue provides its own card styling.
          wrapperClass: TAB_PANE_ROW
        },
        {
          key: 'judge-performance',
          label: 'Judge Performance',
          component: judgePerformance,
          show: this.hasMemberDetails && this.isPrivileged && this.showJudgePerformanceRating,
          props: { memberId: this.memberDetails.id },
          wrapperClass: TAB_PANE_CARD
        },
        {
          key: 'boy-history',
          label: 'BOY History',
          component: memberProfileBoyHistory,
          show: this.isPrivileged,
          props: { memberID: this.$route.params.slug },
          wrapperClass: TAB_PANE_CARD
        }
      ]
    },
    visibleTabs() {
      return this.tabs.filter(t => t.show)
    },
    currentTab() {
      return this.visibleTabs.find(t => t.key === this.selectedTab) || this.visibleTabs[0] || null
    }
  },
  methods: {
    selectTab(key) {
      this.selectedTab = key
      if (this.$route.hash !== `#${key}`) {
        this.$router.replace({ hash: `#${key}` }).catch(() => {})
      }
    }
  },
  watch: {
    '$route' (to, from) { // eslint-disable-line
      if (to.params.slug !== from.params.slug) {
        // Navigating to a different member — reset and refetch
        this.selectedTab = 'loading'
        this.getMemberFromAPI(to.params.slug)
      } else if (to.hash !== from.hash) {
        // Same member, tab changed (e.g. browser back/forward) — no refetch needed
        this.selectedTab = to.hash ? to.hash.slice(1) : 'details'
      }
    },
    // After getting user details from back-end, change tab off loading
    memberDetails() {
      if (this.selectedTab === 'loading') {
        this.selectedTab = this.$route.hash ? this.$route.hash.slice(1) : 'details'
      }
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/views/Members/Profile/MemberProfileMembers.vue